import { defineStore } from 'pinia'
import propertyReportApi from '@/api/propertyreport'
import type {
  AccessUser,
  ExpenseBenchmarksResponse,
  PropertyDetailsResponse,
  RentSourceSubscriptionAttributes,
  RecommendationResponse,
  PricingResponse,
  PricingSettings,
  Similarities,
  SimpleProperty
} from 'common'
import { useUserStore } from './user'
import { coreClient } from '@/api/coreclient'
import { notStarted, loading, loaded, error, type Loading } from '@/utils/loading'
import rentsourceApi from '@/api/rentsource'

export const usePropertyReportStore = defineStore('propertyreport', {
  state: () => ({
    activeSubscriptions: [] as RentSourceSubscriptionAttributes[],
    activeAdminSubscriptions: [] as RentSourceSubscriptionAttributes[],
    retrievedReports: false as boolean,

    // this is the report that is currently being viewed
    activeReport: notStarted as Loading<RentSourceSubscriptionAttributes>,
    subjectDetail: notStarted as Loading<PropertyDetailsResponse>,
    comparableDetails: {} as Record<string, Loading<PropertyDetailsResponse>>,
    similarities: notStarted as Loading<Record<string, Similarities>>,
    expenseBenchmark: notStarted as Loading<ExpenseBenchmarksResponse>,
    revenueManagement: notStarted as Loading<RecommendationResponse>,
    unitPricings: notStarted as Loading<PricingResponse[]>
  }),
  actions: {
    async refreshList(admin?: boolean) {
      const activeSubscriptions = await propertyReportApi.list(admin)
      this.retrievedReports = true
      this.activeSubscriptions = activeSubscriptions
      for (const report of useUserStore().getRecentReports()) {
        if (!activeSubscriptions.find((_) => _.id.toString() === report.id)) {
          useUserStore().deleteRecentReport(report.id.toString())
        }
      }
    },
    async refreshAdminList() {
      this.activeAdminSubscriptions = await propertyReportApi.list(true)
      this.retrievedReports = true
    },
    async getReport(
      id?: number,
      publicId?: string
    ): Promise<Loading<RentSourceSubscriptionAttributes>> {
      this.activeReport = loading
      return await propertyReportApi
        .getReport(id, publicId)
        .then((result) => {
          this.activeReport = loaded(result)
          return this.activeReport
        })
        .catch((e) => {
          this.activeReport = error(e)
          return this.activeReport
        })
    },
    async getSubjectDetail(id: string): Promise<Loading<PropertyDetailsResponse>> {
      this.subjectDetail = loading
      return await rentsourceApi
        .details(id)
        .then((result) => {
          this.subjectDetail = loaded(result)
          return this.subjectDetail
        })
        .catch((err) => {
          this.subjectDetail = error(err)
          return this.subjectDetail
        })
    },
    async getComparableDetails(id: string): Promise<Loading<PropertyDetailsResponse>> {
      this.comparableDetails[id] = loading
      return rentsourceApi
        .details(id)
        .then((result) => {
          this.comparableDetails[id] = loaded(result)
          return this.comparableDetails[id]
        })
        .catch((err) => {
          this.comparableDetails[id] = error(err)
          return this.comparableDetails[id]
        })
    },
    async getSimilarities(
      subjectProperty: PropertyDetailsResponse | SimpleProperty,
      parameters: {
        excludedIds: string[]
        selectedIds: string[]
        topN: number
        renovations?: number
        maxDistance?: number
        minNumberUnits?: number
        maxNumberUnits?: number
        minYearBuilt?: number
        maxYearBuilt?: number
        minNumberStories?: number
        maxNumberStories?: number
        useEmbedding?: boolean
        isAffordable?: boolean
        isSenior?: boolean
        isStudent?: boolean
      }
    ): Promise<Loading<Record<string, Similarities>>> {
      this.similarities = loading
      return await rentsourceApi
        .comparables(subjectProperty, parameters)
        .then((result) => {
          const similarities = {} as Record<string, Similarities>
          result.comparables.forEach((comp) => {
            similarities[comp.id] = comp.similarity_score
          })
          this.similarities = loaded(similarities)
          return this.similarities
        })
        .catch((err) => {
          console.error(err)
          this.similarities = error(err)
          return this.similarities
        })
    },
    async getRevenueManagement(
      subject: PropertyDetailsResponse,
      comparables: PropertyDetailsResponse[],
      parameters: {
        target_occupancy: number
        max_diff_percent: number
        timeframe_days: number
      }
    ): Promise<Loading<RecommendationResponse>> {
      this.revenueManagement = loading
      return rentsourceApi
        .revenueManagement({
          subject: subject,
          comparables: comparables,
          parameters
        })
        .then((response) => {
          this.revenueManagement = loaded(response)
          return this.revenueManagement
        })
        .catch((err) => {
          console.error(err)
          this.revenueManagement = error(err)
          return this.revenueManagement
        })
    },
    async getOldRevenueManagement(
      subject: PropertyDetailsResponse,
      settings: PricingSettings
    ): Promise<Loading<PricingResponse[]>> {
      this.unitPricings = loading
      return await rentsourceApi
        .pricing({
          subject,
          settings
        })
        .then((pricing) => {
          this.unitPricings = loaded(pricing)
          return this.unitPricings
        })
        .catch((err) => {
          console.error(err)
          this.unitPricings = error(err)
          return this.unitPricings
        })
    },
    async getExpenseBenchmarks(
      subject: PropertyDetailsResponse,
      customGPR?: number
    ): Promise<Loading<ExpenseBenchmarksResponse>> {
      this.expenseBenchmark = loading
      return rentsourceApi
        .expenseBenchmarks(subject, customGPR)
        .then((benchmarks) => {
          this.expenseBenchmark = loaded(benchmarks)
          return this.expenseBenchmark
        })
        .catch((err) => {
          console.error(err)
          this.expenseBenchmark = error(new Error('Failed to get expense benchmarks'))
          return this.expenseBenchmark
        })
    },
    cleanReportData() {
      console.log('cleaning report data from store')
      this.activeReport = notStarted
      this.subjectDetail = notStarted
      this.comparableDetails = {}
      this.similarities = notStarted
      this.expenseBenchmark = notStarted
      this.revenueManagement = notStarted
      this.unitPricings = notStarted
    },
    async getByTag(tag: string) {
      if (this.activeSubscriptions.length) {
        return this.activeSubscriptions.filter((_) => _.tags.includes(tag))
      }
      return this.refreshList().then(() => {
        return this.activeSubscriptions.filter((_) => _.tags.includes(tag))
      })
    },
    async update(report: RentSourceSubscriptionAttributes) {
      const data = await propertyReportApi.update(report)
      const existingRecordIndex = this.activeSubscriptions.findIndex((_) => _.id === report.id)
      if (existingRecordIndex > -1 && data) {
        this.activeSubscriptions = [data]
          .concat(this.activeSubscriptions.slice(0, existingRecordIndex))
          .concat(this.activeSubscriptions.slice(existingRecordIndex + 1))
      }
      return data
    },
    async delete(id: number) {
      return propertyReportApi.delete(id).then(() => {
        const index = this.activeSubscriptions.findIndex((_) => _.id === id)
        this.activeSubscriptions.splice(index, 1)
        useUserStore().deleteRecentReport(id.toString())
      })
    },
    async create(report: RentSourceSubscriptionAttributes) {
      const client = await coreClient()
      return await client.propertyReport
        .create(report)
        .then((response) => {
          if (response.data !== null) {
            this.activeSubscriptions = [response.data].concat(this.activeSubscriptions)
            return response.data
          }
          return null
        })
        .catch((e) => {
          console.error(e)
          return null
        })
    },
    async sendEmail(id: number) {
      return propertyReportApi.sendEmail(id)
    },
    async getAccess(reportId: number) {
      return propertyReportApi.getAccess(reportId)
    },
    async createAccess(
      reportId: number,
      notify: boolean,
      data: { email: string; user_id: string | null }[]
    ) {
      return propertyReportApi.createAccess(reportId, notify, data)
    },
    async deleteAccess(reportId: number, userId: string) {
      return propertyReportApi.deleteAccess(reportId, userId)
    },
    removeReport(reportId: number) {
      const idx = this.activeSubscriptions.findIndex((_) => _.id === reportId)
      this.activeSubscriptions.splice(idx, 1)
    },
    updateAccessUsers(report: RentSourceSubscriptionAttributes, accessUsers: AccessUser[]) {
      if (this.activeSubscriptions.length === 0) {
        return
      }

      const idx = this.activeSubscriptions.findIndex((_) => {
        return _.id === report.id
      })

      if (idx === -1) {
        return
      }

      const accessUsersExcludingOwner = accessUsers.filter((u) => u.id !== report.user_id)
      this.activeSubscriptions[idx].access_users = accessUsersExcludingOwner
    }
  },
  getters: {
    existingReports(state): RentSourceSubscriptionAttributes[] {
      return state.activeSubscriptions
    }
  }
})
